import { LevelBuilder } from "./builder.ts";
import { EN_ARCHER, EN_METEOR, EN_NINJA } from "../entities/enemy.ts";
import { diagonalStream } from "./patterns.ts";
import type { Level, SpawnContext } from "./levelTypes.ts";

/**
 * LEVEL 3 — "CARVE"
 * The sword comes online, and the archers own the level: they post up from
 * every edge — top, bottom, left, right — in ever-bolder numbers and must be
 * dueled or endured. The level opens with a real METEOR SHOWER: twenty
 * seconds of rocks that start as a sparse drizzle and build to a fast,
 * paired-up storm from every angle between vertical and horizontal. The
 * first ninja gets a solo spotlight so its dash is legible, and the level
 * ends on a grand finale: seven ninjas, one every three seconds, pouring in
 * under archer fire and flying meteors. No bars, no hen: just dots, meteors,
 * archers, and ninjas.
 */
export function buildLevel3(): Level {
  const b = new LevelBuilder(3, "CARVE", "Sword", 96);

  // L3 meteors: any angle from vertical through horizontal. Steep ones fall
  // from the top; flat ones knife straight across from a side edge.
  const meteor = (ctx: SpawnContext, speed: number): void => {
    const dir = ctx.rng.sign();
    const ang = ctx.rng.range(0, Math.PI * 0.5); // 0 = down, PI/2 = horizontal
    const vx = dir * Math.sin(ang) * speed;
    const vy = Math.cos(ang) * speed;
    if (ang > Math.PI * 0.35) {
      // Mostly horizontal: enter from the side it's travelling away from.
      ctx.enemy(EN_METEOR, dir > 0 ? -50 : ctx.arenaW + 50, ctx.arenaH * ctx.rng.range(0.12, 0.6), {
        vx,
        vy,
      });
    } else {
      ctx.enemy(EN_METEOR, ctx.arenaW * ctx.rng.range(0.1, 0.9), -50, { vx, vy });
    }
  };

  // An archer that flies in from a named edge and posts at (fx, fy).
  type Side = "top" | "bottom" | "left" | "right";
  const archer = (ctx: SpawnContext, side: Side, fx: number, fy: number): void => {
    const hx = ctx.arenaW * fx;
    const hy = ctx.arenaH * fy;
    let x = hx;
    let y = hy;
    if (side === "top") y = -40;
    else if (side === "bottom") y = ctx.arenaH + 40;
    else if (side === "left") x = -40;
    else x = ctx.arenaW + 40;
    ctx.enemy(EN_ARCHER, x, y, { homeX: hx, homeY: hy });
  };

  const ninja = (ctx: SpawnContext, fx: number, fy: number): void => {
    ctx.enemy(EN_NINJA, ctx.arenaW * fx, ctx.arenaH * fy, {});
  };

  // --- METEOR SHOWER (2-22s): the sword's welcome party. Starts as a sparse
  // drizzle of slow rocks, tightens to every beat, speeds up, and ends in
  // paired volleys — sword practice that escalates into a real storm. A lone
  // archer posts up mid-shower to vary the targets. ---
  b.every(2, 21, 1, (ctx, t) => {
    const phase = (t - 2) / 19; // 0 → 1 across the shower
    const k = Math.round(t - 2);
    if (phase < 0.3 && k % 2 === 1) return; // sparse half-cadence opening
    meteor(ctx, 190 + phase * 90); // 190 → 280 as the storm builds
    if (phase > 0.6 && k % 3 === 0) meteor(ctx, 210 + phase * 70); // late pairs
  });
  b.at(10, (ctx) => archer(ctx, "top", 0.5, 0.16));

  // --- Build (22-42s): archers post up from all four sides ---
  b.cue(22, "intensity:1");
  b.at(24, (ctx) => archer(ctx, "left", 0.2, 0.3));
  b.at(26, (ctx) => archer(ctx, "right", 0.8, 0.22));
  b.at(30, (ctx) => archer(ctx, "bottom", 0.35, 0.72));
  b.at(34, (ctx) => archer(ctx, "top", 0.6, 0.18));
  b.every(25, 40, 3.5, (ctx) => meteor(ctx, 240));
  b.every(26, 40, 1.5, (ctx, t) => {
    const k = Math.round((t - 26) / 1.5);
    const x = (k * 127) % ctx.arenaW;
    diagonalStream(ctx, x, -20, k % 2 ? Math.PI * 0.6 : Math.PI * 0.4, 320, 10);
  });

  // --- Solo ninja (44-53s): a clear spotlight so its dash reads. The build
  // archers have wandered off by now; nothing else spawns in this window. ---
  b.cue(44, "intensity:2");
  b.every(44, 53, 1.6, (ctx, t) => {
    // Just a light dot trickle so the stage isn't empty.
    const k = Math.round((t - 44) / 1.6);
    const x = (k * 149) % ctx.arenaW;
    diagonalStream(ctx, x, -20, k % 2 ? Math.PI * 0.6 : Math.PI * 0.4, 300, 10);
  });
  b.at(48, (ctx) => ninja(ctx, 0.5, 0.35));

  // --- Rebuild (54-63s): archers flood back in from the sides + meteors ---
  b.at(54, (ctx) => archer(ctx, "left", 0.18, 0.28));
  b.at(56, (ctx) => archer(ctx, "right", 0.82, 0.32));
  b.at(60, (ctx) => archer(ctx, "bottom", 0.6, 0.7));
  b.every(54, 63, 2.6, (ctx) => meteor(ctx, 270));
  b.every(55, 63, 1.1, (ctx, t) => {
    const k = Math.round((t - 55) / 1.1);
    const x = (k * 113) % ctx.arenaW;
    diagonalStream(ctx, x, -20, k % 2 ? Math.PI * 0.64 : Math.PI * 0.36, 350, 10);
  });

  // --- GRAND FINALE (64-82s): seven ninjas, one every three seconds, under
  // archer fire and flying meteors. ---
  b.cue(64, "intensity:3");
  b.every(64, 82, 3, (ctx, t) => {
    const k = Math.round((t - 64) / 3); // 0..6 → seven ninjas
    const fx = 0.2 + (k % 4) * 0.2; // walk their landing lanes across the field
    const fy = 0.28 + (k % 3) * 0.08;
    ninja(ctx, fx, fy);
  });
  b.at(66, (ctx) => archer(ctx, "top", 0.5, 0.14));
  b.at(76, (ctx) => archer(ctx, "top", 0.4, 0.16));
  b.every(65, 84, 2.2, (ctx) => meteor(ctx, 300));
  b.every(66, 84, 0.8, (ctx, t) => {
    const k = Math.round((t - 66) / 0.8);
    const x = (k * 131) % ctx.arenaW;
    diagonalStream(ctx, x, -20, k % 2 ? Math.PI * 0.68 : Math.PI * 0.32, 380, 9);
  });

  // --- Ramp down (music strips back; enemies wind down at the timeline end) ---
  b.cue(89, "phase:clear");
  return b.build();
}
